import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Shield, Trash2, Key, ToggleLeft, ToggleRight, Eye, RefreshCw, Sparkles, Download, DollarSign, Building2 } from "lucide-react";
import CreateUserDialog from "@/components/CreateUserDialog";
import ManualPasswordResetDialog from "@/components/ManualPasswordResetDialog";
import PromptManager from "@/components/PromptManager";
import EnrichmentQueueManager from "@/components/EnrichmentQueueManager";
import APIUsageMonitor from "@/components/APIUsageMonitor";
import AdminCompanyProfile from "@/components/AdminCompanyProfile";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [maxReEnrichCount, setMaxReEnrichCount] = useState(2);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [recalculatingScores, setRecalculatingScores] = useState(false);
  const [deletingLeads, setDeletingLeads] = useState(false);
  const [deletingCompanies, setDeletingCompanies] = useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
    fetchSettings();
  }, [search]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?query=${search}`, {
        withCredentials: true
      });
      setUsers(response.data.users);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch users");
      if (error.response?.status === 403) {
        navigate("/");
      }
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`, {
        withCredentials: true
      });
      setAuditLogs(response.data.logs);
    } catch (error) {
      console.error("Failed to fetch audit logs");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/max_re_enrichment_count`, {
        withCredentials: true
      });
      setMaxReEnrichCount(response.data.setting_value || 2);
    } catch (error) {
      // Setting doesn't exist yet, use default
      console.log("Settings not found, using default");
    }
  };

  const updateMaxReEnrichCount = async () => {
    setLoadingSettings(true);
    try {
      await axios.put(
        `${API}/admin/settings/max_re_enrichment_count`,
        {
          setting_value: maxReEnrichCount,
          description: "Maximum number of times a lead can be re-enriched"
        },
        { withCredentials: true }
      );
      toast.success("Max re-enrichment count updated successfully!");
    } catch (error) {
      toast.error("Failed to update settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  const recalculateAllScores = async () => {
    if (!window.confirm("This will recalculate lead scores for ALL leads in the system. Continue?")) {
      return;
    }

    setRecalculatingScores(true);
    try {
      const response = await axios.post(
        `${API}/admin/scoring/recalculate-all`,
        {},
        { withCredentials: true }
      );
      toast.success(`Successfully recalculated scores for ${response.data.updated_count} leads!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recalculate scores");
    } finally {
      setRecalculatingScores(false);
    }
  };

  const bulkDeleteLeads = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL leads in the system. This action CANNOT be undone. Are you absolutely sure?")) {
      return;
    }
    
    if (!window.confirm("This is your final confirmation. Type 'DELETE ALL LEADS' in your mind and click OK to proceed.")) {
      return;
    }

    setDeletingLeads(true);
    try {
      const response = await axios.delete(`${API}/admin/bulk-delete/leads`, {
        withCredentials: true
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete leads");
    } finally {
      setDeletingLeads(false);
    }
  };

  const bulkDeleteCompanies = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL companies in the system. This action CANNOT be undone. Are you absolutely sure?")) {
      return;
    }
    
    if (!window.confirm("This is your final confirmation. Type 'DELETE ALL COMPANIES' in your mind and click OK to proceed.")) {
      return;
    }

    setDeletingCompanies(true);
    try {
      const response = await axios.delete(`${API}/admin/bulk-delete/companies`, {
        withCredentials: true
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete companies");
    } finally {
      setDeletingCompanies(false);
    }
  };

  const bulkDeleteContacts = async () => {
    if (!window.confirm("⚠️ WARNING: This will permanently delete ALL contacts in the system. This action CANNOT be undone. Are you absolutely sure?")) {
      return;
    }
    
    if (!window.confirm("This is your final confirmation. Type 'DELETE ALL CONTACTS' in your mind and click OK to proceed.")) {
      return;
    }

    setDeletingContacts(true);
    try {
      const response = await axios.delete(`${API}/admin/bulk-delete/contacts`, {
        withCredentials: true
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contacts");
    } finally {
      setDeletingContacts(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        withCredentials: true
      });
      toast.success("User deleted successfully");
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, {
        is_active: !currentStatus
      }, { withCredentials: true });
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleToggleExportUser = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, {
        is_export_user: !currentStatus
      }, { withCredentials: true });
      toast.success(`Export permission ${!currentStatus ? 'granted' : 'revoked'}`);
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error("Failed to update export permission");
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowPasswordReset(true);
  };

  const handleImpersonate = async (user) => {
    if (!window.confirm(`Are you sure you want to log in as ${user.email}?`)) return;

    try {
      const response = await axios.post(
        `${API}/admin/users/${user.id}/impersonate`,
        {},
        { withCredentials: true }
      );
      
      // Update auth context with the impersonated user
      login(response.data.user);
      
      toast.success(`Now logged in as ${user.email}`);
      
      // Navigate to home page
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6 fade-in p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Admin Panel</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage users and view audit logs</p>
        </div>
        <Button onClick={() => navigate('/')} variant="outline" className="w-full sm:w-auto">
          Back to App
        </Button>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="flex flex-wrap gap-1">
          <TabsTrigger value="users" className="flex-1 sm:flex-none">Users</TabsTrigger>
          <TabsTrigger value="audit" className="flex-1 sm:flex-none">Audit Logs</TabsTrigger>
          <TabsTrigger value="settings" className="flex-1 sm:flex-none">Settings</TabsTrigger>
          <TabsTrigger value="company-profile" className="flex-1 sm:flex-none">
            <Building2 className="w-4 h-4 mr-2 hidden sm:inline" />
            <span className="hidden sm:inline">Company Profile</span>
            <span className="sm:hidden">Company</span>
          </TabsTrigger>
          <TabsTrigger value="enrichment">
            <RefreshCw className="w-4 h-4 mr-2" />
            Enrichment Queue
          </TabsTrigger>
          <TabsTrigger value="api-usage">
            <DollarSign className="w-4 h-4 mr-2" />
            API Usage & Costs
          </TabsTrigger>
          <TabsTrigger value="prompts">
            <Sparkles className="w-4 h-4 mr-2" />
            Prompt Manager
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>

          <div className="glass-card rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>Last Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {user.first_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {user.last_name || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {user.role === 'admin' ? (
                          <Badge className="bg-purple-100 text-purple-700 w-fit">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 w-fit">User</Badge>
                        )}
                        {user.is_export_user && (
                          <Badge className="bg-blue-100 text-blue-700 w-fit text-xs">
                            <Download className="w-3 h-3 mr-1" />
                            Export User
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        {user.role !== 'admin' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleExportUser(user.id, user.is_export_user)}
                              title={user.is_export_user ? "Revoke Export Permission" : "Grant Export Permission"}
                              className={user.is_export_user ? "text-blue-600" : ""}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleImpersonate(user)}
                              title="Login As User"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleActive(user.id, user.is_active)}
                          title={user.is_active ? "Deactivate" : "Activate"}
                        >
                          {user.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleResetPassword(user)}
                          title="Set Password"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <div className="glass-card rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">{formatDate(log.created_at)}</TableCell>
                    <TableCell className="font-medium">{log.actor_email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{log.action.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{log.target_email || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="company-profile" className="space-y-4">
          <AdminCompanyProfile />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <div className="glass-card rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4">System Settings</h2>
            
            <div className="space-y-6">
              <div className="border-b pb-4">
                <h3 className="text-lg font-medium mb-2">Enrichment Settings</h3>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Max Re-Enrichment Count
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                      Maximum number of times a lead can be re-enriched after initial enrichment
                    </p>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={maxReEnrichCount}
                      onChange={(e) => setMaxReEnrichCount(parseInt(e.target.value) || 0)}
                      className="w-32"
                    />
                  </div>
                  <Button 
                    onClick={updateMaxReEnrichCount}
                    disabled={loadingSettings}
                    className="mt-6"
                  >
                    {loadingSettings ? "Saving..." : "Save Setting"}
                  </Button>
                </div>
              </div>

              <div className="border-b pb-4">
                <h3 className="text-lg font-medium mb-2">Lead Scoring</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Recalculate rule-based lead scores for all leads in the system. Scores are calculated based on data completeness, enrichment quality, lead stage, and relationships.
                </p>
                <Button 
                  onClick={recalculateAllScores}
                  disabled={recalculatingScores}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {recalculatingScores ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Recalculating Scores...
                    </>
                  ) : (
                    "Recalculate All Lead Scores"
                  )}
                </Button>
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600">
                    <strong>Scoring Algorithm (0-100):</strong>
                    <br />
                    • Data Completeness: 40 points (name, email, phone, company, title)
                    <br />
                    • Enrichment Quality: 20 points (enriched status, company size, industry)
                    <br />
                    • Lead Stage: 20 points (new to won progression)
                    <br />
                    • Relationships: 20 points (company and contact links)
                  </p>
                </div>
              </div>

              <div className="border-b pb-4">
                <h3 className="text-lg font-medium mb-2 text-red-600">⚠️ Danger Zone - Data Management</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Permanently delete all data from the system. These actions CANNOT be undone.
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Delete All Leads</p>
                        <p className="text-sm text-slate-600">Permanently remove all leads from the database</p>
                      </div>
                      <Button 
                        onClick={bulkDeleteLeads}
                        disabled={deletingLeads}
                        variant="destructive"
                      >
                        {deletingLeads ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete All Leads
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Delete All Companies</p>
                        <p className="text-sm text-slate-600">Permanently remove all companies from the database</p>
                      </div>
                      <Button 
                        onClick={bulkDeleteCompanies}
                        disabled={deletingCompanies}
                        variant="destructive"
                      >
                        {deletingCompanies ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete All Companies
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">Delete All Contacts</p>
                        <p className="text-sm text-slate-600">Permanently remove all contacts from the database</p>
                      </div>
                      <Button 
                        onClick={bulkDeleteContacts}
                        disabled={deletingContacts}
                        variant="destructive"
                      >
                        {deletingContacts ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete All Contacts
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>


        <TabsContent value="enrichment" className="space-y-4">
          <EnrichmentQueueManager />
        </TabsContent>

        <TabsContent value="api-usage" className="space-y-4">
          <APIUsageMonitor />
        </TabsContent>

        <TabsContent value="prompts" className="space-y-4">
          <PromptManager />
        </TabsContent>
      </Tabs>

      {showCreateUser && (
        <CreateUserDialog
          open={showCreateUser}
          onClose={() => setShowCreateUser(false)}
          onSuccess={() => {
            fetchUsers();
            fetchAuditLogs();
          }}
        />
      )}

      {showPasswordReset && selectedUser && (
        <ManualPasswordResetDialog
          open={showPasswordReset}
          onClose={() => {
            setShowPasswordReset(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSuccess={() => {
            fetchUsers();
            fetchAuditLogs();
          }}
        />
      )}
    </div>
  );
};

export default AdminPanel;
